import { AxiosError } from "axios";

import {
  QueryStatus,
  QueryEnvelope,
  EnsuredQueryEnvelope,
} from "../../../../services/query";
import { Operation } from "../../../../models";

import { useMaybeCurrentOperationId } from "../currentOperationId";
import {
  useOperationStatus,
  useOperationError,
  useOperationData,
  useMaybeOperationData,
  useMaybeOperation,
  useOperation,
} from "../operation";

import {
  useMaybeOperation as useMaybeOperationV4,
  useOperation as useOperationV4,
} from "../operationV4";
import { OperationV4 } from "../../../../models/operations";

export {
  useCurrentOperationGlobalActivity,
  useCurrentOperationGlobalStatus,
} from "./currentOperation.globalStatus";

export * from "./currentOperation.invalidate";

// ###############################
// ## useCurrentOperationStatus ##
// ###############################

export type ResultOfUseCurrentOperationStatus = QueryStatus;
export type UseCurrentOperationStatus = () => ResultOfUseCurrentOperationStatus;

/**
 * The returned value of this hook will contain the status
 * of the fetching of the current operation data.
 */
export const useCurrentOperationStatus: UseCurrentOperationStatus = () => {
  const currentOperationId = useMaybeCurrentOperationId();

  return useOperationStatus(currentOperationId);
};

// ##############################
// ## useCurrentOperationError ##
// ##############################

export type ResultOfUseCurrentOperationError = AxiosError | null;
export type UseCurrentOperationError = () => ResultOfUseCurrentOperationError;

/**
 * The returned value of this hook will contain the error
 * that has occurred in the fetching of the current operation data
 * or null if no error occurred.
 */
export const useCurrentOperationError: UseCurrentOperationError = () => {
  const currentOperationId = useMaybeCurrentOperationId();

  return useOperationError(currentOperationId);
};

// ##################################
// ## useMaybeCurrentOperationData ##
// ##################################

export type ResultOfUseMaybeCurrentOperationData = Operation | undefined;
export type UseMaybeCurrentOperationData =
  () => ResultOfUseMaybeCurrentOperationData;

/**
 * The returned value of this hook will contain
 * the current operation data, which may be undefined.
 *
 * Use this over the operation from `useOperations` as this hook
 * will not trigger a re-render for you unless the current operation
 * and only the current operation changes.
 */
export const useMaybeCurrentOperationData: UseMaybeCurrentOperationData =
  () => {
    const currentOperationId = useMaybeCurrentOperationId();

    return useMaybeOperationData({ operationId: currentOperationId });
  };

// #############################
// ## useCurrentOperationData ##
// #############################

export type ResultOfUseCurrentOperationData = Operation;
export type UseCurrentOperationData = () => ResultOfUseCurrentOperationData;

/**
 * The returned value of this hook will contain the current operation data.
 *
 * This hook is guaranteed to contain the current operation data or will throw.
 *
 * Use this over the operation from `useOperations` as this hook
 * will not trigger a re-render for you unless the current operation data
 * and only the current operation data changes.
 */
export const useCurrentOperationData: UseCurrentOperationData = () => {
  const currentOperationId = useMaybeCurrentOperationId();

  return useOperationData({ operationId: currentOperationId });
};

// ##############################
// ## useMaybeCurrentOperation ##
// ##############################

export type ResultOfUseMaybeCurrentOperation = QueryEnvelope<Operation>;
export type UseMaybeCurrentOperation = () => ResultOfUseMaybeCurrentOperation;

/**
 * The `data` property returned from this hook will contain
 * the current operation data, which may be undefined
 *
 * The `error` and `status` properties returned from this hook
 * will reflect the state of the current operation data.
 */
export const useMaybeCurrentOperation: UseMaybeCurrentOperation = () => {
  const currentOperationId = useMaybeCurrentOperationId();

  return useMaybeOperation({
    operationId: currentOperationId,
  });
};

// #########################
// ## useCurrentOperation ##
// #########################

export type ResultOfUseCurrentOperation = EnsuredQueryEnvelope<Operation>;
export type UseCurrentOperation = () => ResultOfUseCurrentOperation;

/**
 * The `data` property returned from this hook will contain
 * the current operation data.
 *
 * The `error` and `status` properties returned from this hook
 * will reflect the state of the current operation data.
 *
 * This hook is guaranteed to contain current operation data or will throw.
 */
export const useCurrentOperation: UseCurrentOperation = () => {
  const currentOperationId = useMaybeCurrentOperationId();

  return useOperation({
    operationId: currentOperationId,
  });
};

// ##############################
// ## useMaybeCurrentOperation ##
// ##############################

export type ResultOfUseMaybeCurrentOperationV4 = QueryEnvelope<OperationV4>;
export type UseMaybeCurrentOperationV4 =
  () => ResultOfUseMaybeCurrentOperationV4;

/**
 * The `data` property returned from this hook will contain
 * the current operation data, which may be undefined
 *
 * The `error` and `status` properties returned from this hook
 * will reflect the state of the current operation data.
 */
export const useMaybeCurrentOperationV4: UseMaybeCurrentOperationV4 = () => {
  const currentOperationId = useMaybeCurrentOperationId();

  return useMaybeOperationV4({
    operationId: currentOperationId,
  });
};

export type ResultOfUseCurrentOperationV4 = EnsuredQueryEnvelope<OperationV4>;
export type UseCurrentOperationV4 = () => ResultOfUseCurrentOperationV4;

export const useCurrentOperationV4: UseCurrentOperationV4 = () => {
  const currentOperationId = useMaybeCurrentOperationId();

  return useOperationV4({
    operationId: currentOperationId,
  });
};
